<template>
  <div class="shift-calendar-page">
    <!-- Header -->
    <div class="calendar-header">
      <h1 class="page-title">🗓️ {{ $t('navigation.shiftCalendar') }}</h1>
      <div class="calendar-controls">
        <button @click="previousMonth" class="nav-btn">‹</button>
        <h2 class="current-month">{{ formatMonthYear(currentDate) }}</h2>
        <button @click="nextMonth" class="nav-btn">›</button>
      </div>
    </div>

    <!-- Calendar Grid -->
    <div class="calendar-container">
      <div class="calendar-wrapper">
        <div class="calendar-weekdays">
          <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
        </div>
        
        <div class="calendar-grid">
          <div 
            v-for="day in calendarDays" 
            :key="day.date" 
            class="calendar-day"
            :class="{ 
              'other-month': day.isOtherMonth,
              'today': day.isToday,
              'has-shifts': day.shifts.length > 0,
              'selected': selectedDate && day.date === formatDate(selectedDate)
            }"
            @click="selectDay(day)"
          >
            <div class="day-number">{{ day.dayNumber }}</div>
            
            <!-- Shift indicators -->
            <div class="shift-indicators" v-if="day.shifts.length > 0">
              <div 
                v-for="shift in day.shifts.slice(0, 3)" 
                :key="shift.id"
                class="shift-indicator"
                :class="{ 
                  'shift-indicator-empty': !shift.assignments || shift.assignments.length === 0,
                  'shift-indicator-pending': hasPendingAssignments(shift),
                  'shift-indicator-rejected': hasRejectedAssignments(shift)
                }"
                :title="`${formatTime(shift.start_time)} - ${formatTime(shift.end_time)}${getShiftWorkerNames(shift) ? ' | ' + getShiftWorkerNames(shift) : ''}`"
              >
                <div class="shift-info">
                  <span class="shift-workers" v-if="getShiftWorkerInitialsArray(shift).length > 0">
                    <span 
                      v-for="(worker, index) in getShiftWorkerInitialsArray(shift)" 
                      :key="worker.workerId"
                      :class="{ 'sick-worker-initials': worker.isSick }"
                    >
                      {{ worker.initials }}<span v-if="index < getShiftWorkerInitialsArray(shift).length - 1">, </span>
                    </span>
                  </span>
                  <span class="shift-time">{{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}</span>
                </div>
              </div>
              <div v-if="day.shifts.length > 3" class="more-shifts">
                +{{ day.shifts.length - 3 }} more
              </div>
            </div>
            
            <!-- Add shift button -->
            <button 
              v-if="!day.isOtherMonth" 
              @click.stop="openShiftCreation(day)"
              class="add-shift-btn"
              title="Add shift"
            >
              +
            </button>
          </div>
        </div>
      </div>

      <!-- Day Details Panel (Always Visible) -->
      <div class="day-details">
        <div class="day-header">
          <span class="date-icon">📅</span>
          <h3>{{ formatFullDate(selectedDate) }}</h3>
        </div>

      <div class="day-shifts">
        <div v-if="selectedDayShifts.length === 0" class="no-shifts">
          <p>{{ $t('shiftCalendar.noShiftsScheduled') }}</p>
          <button @click="openShiftCreation(selectedDay)" class="create-shift-btn">
            {{ $t('shiftCalendar.createShift') }}
          </button>
        </div>
        
        <div v-else class="shifts-list">
          <div 
            v-for="shift in selectedDayShifts" 
            :key="shift.id"
            class="shift-card"
            :class="{ 
              'shift-card-empty': !shift.assignments || shift.assignments.length === 0,
              'shift-card-pending': hasPendingAssignments(shift)
            }"
          >
            <div class="shift-header">
              <div class="shift-time-range">
                {{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}
              </div>
              <div class="shift-meta">
                <div class="shift-duration">
                  {{ $t('shiftCalendar.duration') }}: {{ calculateShiftDuration(shift.start_time, shift.end_time) }}
                </div>
                <div class="shift-break" v-if="getShiftBreakMinutes(shift.start_time, shift.end_time) > 0">
                  🕐 {{ $t('shiftCalendar.break') }}: {{ getShiftBreakMinutes(shift.start_time, shift.end_time) }} min
                </div>
              </div>
            </div>
            
            <div class="shift-assignments" v-if="shift.assignments && shift.assignments.length > 0">
              <h4 class="workers-header">{{ $t('shiftCalendar.assignedWorkers') }} ({{ shift.assignments.length }})</h4>
              <div class="worker-cards">
                <div 
                  v-for="assignment in shift.assignments" 
                  :key="assignment.id"
                  class="worker-card"
                  :class="{ 
                    'has-sick-leave': assignment.sick_leave_type,
                    'has-partial-absence': assignment.partial_absence,
                    'has-unauthorized-absence': assignment.unauthorized_absence,
                    'status-pending': assignment.status === 'pending',
                    'status-rejected': assignment.status === 'rejected',
                    'worker-sick-leave': hasSickLeaveOnDate(assignment.worker.id, shift.start_time?.split('T')[0])
                  }"
                >
                  <div class="worker-info">
                    <span class="worker-name">
                      {{ assignment.worker.first_name }} {{ assignment.worker.last_name }}
                      <span v-if="hasSickLeaveOnDate(assignment.worker.id, shift.start_time?.split('T')[0])" class="sick-leave-token">
                        🤒
                      </span>
                    </span>
                    <span v-if="assignment.status === 'pending'" class="status-badge pending">⏳ Pending Approval</span>
                    <span v-if="assignment.status === 'rejected'" class="status-badge rejected">❌ Rejected</span>
                  </div>
                  
                  <!-- Remove Worker Button -->
                  <button 
                    class="remove-worker-btn" 
                    @click="removeWorkerFromShift(assignment, shift)"
                  >
                    {{ $t('shiftCalendar.removeWorker') }}
                  </button>
                </div>
              </div>
            </div>
            
            <div v-else class="no-workers">
              <span>{{ $t('shiftCalendar.noWorkers') }}</span>
            </div>
            
            <!-- Action Buttons Row -->
            <div class="shift-actions">
              <button @click="openAddWorkers(shift)" class="btn-add-workers">
                {{ shift.assignments && shift.assignments.length > 0 ? $t('shiftCalendar.addMoreWorkers') : $t('shiftCalendar.addWorkers') }}
              </button>
              <button @click="deleteShift(shift.id)" class="btn-delete-shift">
                {{ $t('shiftCalendar.deleteShift') }}
              </button>
            </div>
          </div>
          
          <button @click="openShiftCreation(selectedDay)" class="create-shift-btn">
            {{ $t('shiftCalendar.addAnotherShift') }}
          </button>
        </div>
      </div>
      </div>
    </div>

    <!-- Shift Creation Modal -->
    <div v-if="showShiftCreation" class="modal-overlay" @click="closeShiftCreation">
      <div class="modal-content" @click.stop>
        <ShiftCreation 
          :preselected-date="preselectedDate"
          @shift-created="onShiftCreated" 
          @close="closeShiftCreation"
        />
      </div>
    </div>

    <!-- Add Workers Modal -->
    <div v-if="showAddWorkers" class="modal-overlay" @click="closeAddWorkers">
      <div class="modal-content" @click.stop>
        <AddWorkers 
          :shift="shiftToAddWorkers"
          @workers-added="onWorkersAdded" 
          @close="closeAddWorkers"
        />
      </div>
    </div>

    <!-- Delete Worker Modal -->
    <div v-if="showDeleteWorker" class="modal-overlay" @click="closeDeleteWorker">
      <div class="modal-content" @click.stop>
        <DeleteWorker 
          :shift="shiftToDeleteWorker"
          @worker-deleted="onWorkerDeleted" 
          @close="closeDeleteWorker"
        />
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <span>{{ $t('common.loading') }}</span>
    </div>

    <!-- Messages -->
    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>

    <!-- Custom Modal for confirmations -->
    <CustomModal 
      ref="customModal"
      type="confirm"
      :title="$t('shiftCalendar.deleteShift')"
      :message="$t('shiftCalendar.deleteShiftConfirm')"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      @confirm="confirmDeleteShift"
      @cancel="cancelDeleteShift"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth.js'
import { useShiftStore } from '../stores/shift.js'
import { shiftApi } from '../services/shiftApi.js'
import { useSickLeave } from '../composables/useSickLeave.js'
import { useTimeCalculations, calculateRequiredBreak } from '../composables/useTimeCalculations.js'
import ShiftCreation from './ShiftCreation.vue'
import AddWorkers from './AddWorkers.vue'
import DeleteWorker from './DeleteWorker.vue'
import CustomModal from './CustomModal.vue'

const { t } = useI18n()
const authStore = useAuthStore()
const shiftStore = useShiftStore()
const { monthlySickLeaves, loadSickLeaves, hasSickLeaveOnDate } = useSickLeave()
const { formatTime, calculateShiftDuration } = useTimeCalculations()
const loading = ref(false)
const message = ref('')
const messageType = ref('success')

// Calendar state
const currentDate = ref(new Date())
const selectedDate = ref(new Date()) // Initialize with today's date
const shifts = ref([])
const showShiftCreation = ref(false)
const showAddWorkers = ref(false)
const showDeleteWorker = ref(false)
const preselectedDate = ref('')
const shiftToAddWorkers = ref(null)
const shiftToDeleteWorker = ref(null)
const customModal = ref(null)
const shiftToDelete = ref(null)

// Constants - weekdays for calendar header
const weekdays = computed(() => [
  t('weekdays.mon'),
  t('weekdays.tue'),
  t('weekdays.wed'),
  t('weekdays.thu'),
  t('weekdays.fri'),
  t('weekdays.sat'),
  t('weekdays.sun')
])

// Computed properties
const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  
  // Get first day of month and adjust for Monday start
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  
  // Adjust for Monday start (0 = Monday, 6 = Sunday)
  let startDay = (firstDay.getDay() + 6) % 7
  
  const days = []
  
  // Add days from previous month
  const prevMonthLastDay = new Date(year, month, 0) // Last day of previous month
  const prevMonthLastDate = prevMonthLastDay.getDate()
  
  for (let i = startDay - 1; i >= 0; i--) {
    const dayNumber = prevMonthLastDate - i
    const date = new Date(year, month - 1, dayNumber)
    days.push(createCalendarDay(date, true))
  }
  
  // Add days from current month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day)
    days.push(createCalendarDay(date, false))
  }
  
  // Add days from next month to fill grid
  const remainingDays = 42 - days.length // 6 weeks * 7 days
  for (let day = 1; day <= remainingDays; day++) {
    const date = new Date(year, month + 1, day)
    days.push(createCalendarDay(date, true))
  }
  
  return days
})

const selectedDay = computed(() => {
  if (!selectedDate.value) return null
  return calendarDays.value.find(day => day.date === formatDate(selectedDate.value))
})

const selectedDayShifts = computed(() => {
  if (!selectedDay.value) return []
  return selectedDay.value.shifts.sort((a, b) => 
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  )
})

// Methods
function createCalendarDay(date, isOtherMonth) {
  const dateString = formatDate(date)
  const dayShifts = shifts.value.filter(shift => {
    const shiftDate = new Date(shift.start_time).toISOString().split('T')[0]
    return shiftDate === dateString
  })
  
  const today = new Date()
  const isToday = dateString === formatDate(today)
  
  return {
    date: dateString,
    dayNumber: date.getDate(),
    isOtherMonth,
    isToday,
    shifts: dayShifts
  }
}

function formatDate(date) {
  // Fix: Use local date components to avoid timezone issues
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatMonthYear(date) {
  return date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' })
}

function formatFullDate(date) {
  return date.toLocaleDateString('sv-SE', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

function hasPendingAssignments(shift) {
  return shift.assignments && shift.assignments.some(a => a.status === 'pending')
}

function hasRejectedAssignments(shift) {
  return shift.assignments && shift.assignments.some(a => a.status === 'rejected')
}

function previousMonth() {
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() - 1)
  currentDate.value = newDate
}

function nextMonth() {
  const newDate = new Date(currentDate.value)
  newDate.setMonth(newDate.getMonth() + 1)
  currentDate.value = newDate
}

function selectDay(day) {
  if (day.isOtherMonth) {
    // Navigate to the month containing this day
    const [year, month, dayNum] = day.date.split('-').map(Number)
    currentDate.value = new Date(year, month - 1, dayNum)
  }
  // Fix: Create date object without timezone issues
  const [year, month, dayNum] = day.date.split('-').map(Number)
  selectedDate.value = new Date(year, month - 1, dayNum)
}

function openShiftCreation(day) {
  preselectedDate.value = day.date
  showShiftCreation.value = true
}

function closeShiftCreation() {
  showShiftCreation.value = false
  preselectedDate.value = ''
}

function onShiftCreated(data) {
  showMessage(t('shiftCalendar.shiftCreated'), 'success')
  closeShiftCreation()
  loadShifts() // Refresh the calendar
}

function onWorkersAdded(data) {
  showMessage(t('shiftCalendar.workersAdded'), 'success')
  closeAddWorkers()
  loadShifts() // Refresh the calendar
}

function openAddWorkers(shift) {
  shiftToAddWorkers.value = shift
  showAddWorkers.value = true
}

function closeAddWorkers() {
  showAddWorkers.value = false
  shiftToAddWorkers.value = null
}

function openDeleteWorker(shift) {
  shiftToDeleteWorker.value = shift
  showDeleteWorker.value = true
}

function closeDeleteWorker() {
  showDeleteWorker.value = false
  shiftToDeleteWorker.value = null
}

function onWorkerDeleted() {
  showMessage(t('shiftCalendar.workerRemoved'), 'success')
  closeDeleteWorker()
  loadShifts() // Refresh the calendar
}

async function updateAbsenceStatus(assignment, field, value) {
  try {
    const updates = { [field]: value }
    await shiftApi.updateAssignmentDetails(assignment.id, updates)
    
    // Update local state
    assignment[field] = value
    
    const statusText = field === 'unauthorized_absence' 
      ? (value ? t('shiftCalendar.unauthorizedAbsenceMarked') : t('shiftCalendar.unauthorizedAbsenceRemoved'))
      : t('shiftCalendar.absenceStatusUpdated') + `: ${value || t('common.none')}`
    
    showMessage(statusText, 'success')
  } catch (error) {
    console.error('Error updating absence status:', error)
    showMessage(t('shiftCalendar.failedToUpdateAbsence'), 'error')
  }
}

async function togglePartialAbsence(assignment, isPartial) {
  try {
    const updates = { 
      partial_absence: isPartial,
      // Clear time fields if turning off partial absence
      absence_start: isPartial ? assignment.absence_start : null,
      absence_end: isPartial ? assignment.absence_end : null
    }
    
    await shiftApi.updateAssignmentDetails(assignment.id, updates)
    
    // Update local state
    assignment.partial_absence = isPartial
    if (!isPartial) {
      assignment.absence_start = null
      assignment.absence_end = null
    }
    
    showMessage(isPartial ? t('shiftCalendar.partialAbsenceEnabled') : t('shiftCalendar.fullDayAbsence'), 'success')
  } catch (error) {
    console.error('Error toggling partial absence:', error)
    showMessage(t('shiftCalendar.failedToUpdateAbsenceTime'), 'error')
  }
}

async function deleteShift(shiftId) {
  shiftToDelete.value = shiftId
  // Show custom confirmation modal instead of browser confirm
  customModal.value.show()
}

async function confirmDeleteShift() {
  if (!shiftToDelete.value) return
  
  try {
    loading.value = true
    await shiftApi.deleteShift(shiftToDelete.value)
    showMessage(t('shiftCalendar.shiftDeleted'), 'success')
    await loadShifts()
  } catch (error) {
    showMessage(t('shiftCalendar.failedToDeleteShift') + ': ' + error.message, 'error')
  } finally {
    loading.value = false
    shiftToDelete.value = null
  }
}

function cancelDeleteShift() {
  shiftToDelete.value = null
}

async function removeWorkerFromShift(assignment, shift) {
  try {
    loading.value = true
    await shiftApi.removeWorkerFromShift(assignment.id)
    showMessage(t('shiftCalendar.workerRemoved') || 'Worker removed successfully', 'success')
    await loadShifts()
  } catch (error) {
    showMessage(t('shiftCalendar.failedToRemoveWorker') || 'Failed to remove worker: ' + error.message, 'error')
  } finally {
    loading.value = false
  }
}

async function loadShifts() {
  try {
    loading.value = true
    
    // Calculate date range: current month ±5 days to fill grid edges
    const year = currentDate.value.getFullYear()
    const month = currentDate.value.getMonth()
    
    // Start: 5 days before first day of current month
    const startDate = new Date(year, month, -4) // Day -4 means 5 days before month start
    
    // End: 5 days after last day of current month
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate()
    const endDate = new Date(year, month, lastDayOfMonth + 5)
    
    const shiftsWithAssignments = await shiftApi.getShiftsWithAssignments(startDate, endDate)
    
    shifts.value = shiftsWithAssignments || []
    
    // Load sick leaves for the month
    await loadMonthlySickLeaves()
    
  } catch (error) {
    console.error('Error loading shifts:', error)
    showMessage('Failed to load shifts: ' + error.message, 'error')
    shifts.value = [] // Set empty array on error
  } finally {
    loading.value = false
  }
}

async function loadMonthlySickLeaves() {
  try {
    const year = currentDate.value.getFullYear()
    const month = currentDate.value.getMonth() + 1
    
    // Collect all unique worker IDs from shifts
    const workerIds = new Set()
    shifts.value.forEach(shift => {
      if (shift.assignments) {
        shift.assignments.forEach(assignment => {
          if (assignment.worker?.id) {
            workerIds.add(assignment.worker.id)
          }
        })
      }
    })
    
    // Use composable to load sick leaves
    await loadSickLeaves(Array.from(workerIds), year, month)
  } catch (error) {
    console.error('Failed to load monthly sick leaves:', error)
  }
}

function getShiftWorkerInitialsArray(shift) {
  if (!shift.assignments || shift.assignments.length === 0) return []
  
  // Extract date from shift.start_time (YYYY-MM-DDTHH:MM:SS -> YYYY-MM-DD)
  const shiftDate = shift.start_time ? shift.start_time.split('T')[0] : null
  
  return shift.assignments.map(assignment => {
    const firstName = assignment.worker?.first_name || ''
    const lastName = assignment.worker?.last_name || ''
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    const isSick = shiftDate ? hasSickLeaveOnDate(assignment.worker?.id, shiftDate) : false
    return {
      initials,
      isSick,
      workerId: assignment.worker?.id
    }
  })
}

function getShiftWorkerNames(shift) {
  if (!shift.assignments || shift.assignments.length === 0) return ''
  
  return shift.assignments
    .map(assignment => `${assignment.worker?.first_name || ''} ${assignment.worker?.last_name || ''}`.trim())
    .join(', ')
}

function getShiftBreakMinutes(startTime, endTime) {
  if (!startTime || !endTime) return 0
  
  // Extract time portion if it's a full datetime string
  let start = startTime.includes('T') ? startTime.split('T')[1].substring(0, 5) : startTime
  let end = endTime.includes('T') ? endTime.split('T')[1].substring(0, 5) : endTime
  
  return calculateRequiredBreak(start, end)
}

function showMessage(text, type = 'success') {
  message.value = text
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 3000)
}

// Watchers
watch(currentDate, () => {
  loadShifts()
})

// Watch for shift changes from other components (worker approve/reject, add/remove workers)
watch(() => shiftStore.lastUpdate, () => {
  loadShifts()
})

// Initialize
onMounted(() => {
  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    window.location.href = '/'
    return
  }
  
  loadShifts()
})
</script>

<style scoped>
.shift-calendar-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
}

.calendar-header h1 {
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.nav-btn {
  padding: 0.5rem 1rem;
  background: #667eea;
  color: white;
  font-weight: 700;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.nav-btn:hover {
  background: #5a6fd8;
}

.current-month {
  margin: 0;
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  min-width: 200px;
  text-align: center;
}

.calendar-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  align-items: flex-start;
}

.calendar-wrapper {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: flex 0.3s ease;
  display: flex;
  flex-direction: column;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f8f9fa;
}

.weekday {
  padding: 1rem;
  text-align: center;
  font-weight: 600;
  color: #6b7280;
  border-right: 1px solid #e5e7eb;
}

.weekday:last-child {
  border-right: none;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-day {
  min-height: 120px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  display: flex;
  flex-direction: column;
}

.calendar-day:nth-child(7n) {
  border-right: none;
}

.calendar-day:hover {
  background-color: #f8f9fa;
}

.calendar-day.other-month {
  background-color: #f9f9f9;
  opacity: 0.5;
}

.calendar-day.today {
  background-color: #fff3cd;
  border: 2px solid #ffc107;
}

.calendar-day.selected {
  background-color: #e3f2fd;
  border: 2px solid #2196f3;
}

.calendar-day.has-shifts {
  background-color: #f0f9ff;
}

.day-number {
  font-weight: 600;
  font-size: 1.1rem;
  color: #374151;
  margin-bottom: 0.5rem;
}

.shift-indicators {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shift-indicator {
  background: #667eea;
  color: white;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 22px;
}

.shift-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shift-workers {
  font-size: 0.75rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.25);
  padding: 1px 4px;
  border-radius: 3px;
  align-self: flex-start;
}

.sick-worker-initials {
  background: #ef4444;
  color: white;
  padding: 2px 4px;
  border-radius: 3px;
  text-decoration: line-through;
  text-decoration-color: white;
  text-decoration-thickness: 2px;
  font-weight: 800;
}

.shift-time {
  font-weight: 500;
  font-size: 0.65rem;
}

.more-shifts {
  font-size: 0.7rem;
  color: #6b7280;
  text-align: center;
  margin-top: 2px;
}

.add-shift-btn {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.add-shift-btn:hover {
  background: #059669;
  transform: scale(1.1);
}

.day-details {
  flex: 0 0 25%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  overflow-y: auto;
  scroll-behavior: smooth;
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

.day-details::-webkit-scrollbar {
  width: 8px;
}

.day-details::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.day-details::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.day-details::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.day-details h3 {
  margin: 0 0 1.5rem 0;
  color: #333;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.day-shifts {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.day-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.date-icon {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-shifts {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.shifts-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.shift-card {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.2s;
}

.shift-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.shift-card-empty {
  background: #ffebee;
  border: 2px dashed #ef5350;
  position: relative;
}

.shift-card-pending {
  border: 3px dotted #ff9800 !important;
  background: #fff3e0;
}

.shift-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.shift-time-range {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  flex: 1 1 100%;
}

.shift-meta {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  flex: 1 1 100%;
}

.shift-duration {
  font-size: 0.9rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.shift-break {
  font-size: 0.9rem;
  color: #059669;
  background: #d1fae5;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

.shift-assignments {
  margin: 1rem 0;
}

.workers-header {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
}

.worker-cards {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.worker-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  transition: all 0.2s;
}

.worker-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.worker-card.has-sick-leave {
  border-left: 4px solid #f59e0b;
  background: #fffbeb;
}

.worker-card.has-partial-absence {
  border-left: 4px solid #8b5cf6;
  background: #faf5ff;
}

.worker-card.has-unauthorized-absence {
  border-left: 4px solid #ef4444;
  background: #fef2f2;
}

/* Pending approval status - orange dotted border */
.worker-card.status-pending {
  border: 2px dashed #f97316;
  background: #fff7ed;
}

.worker-card.status-rejected {
  border: 2px solid #ef4444;
  background: #fef2f2;
  opacity: 0.7;
}

/* Sick leave indicator on worker card */
.worker-card.worker-sick-leave {
  border-left: 4px solid #ef4444;
  background: #fee2e2;
}

.sick-leave-token {
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  font-size: 1.2rem;
}

.worker-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  margin-top: 0.25rem;
}

.status-badge.pending {
  background: #fed7aa;
  color: #9a3412;
}

.status-badge.rejected {
  background: #fecaca;
  color: #991b1b;
}

.worker-name {
  font-weight: 600;
  color: #374151;
}

.worker-role {
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: capitalize;
}

.remove-worker-btn {
  padding: 0.25rem;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
  white-space: normal;
  width: 70px;
}

.remove-worker-btn:hover {
  background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
  box-shadow: 0 3px 6px rgba(249, 115, 22, 0.3);
  transform: translateY(-1px);
}

.remove-worker-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
}

.absence-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 250px;
}

.absence-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.absence-time-section {
  margin-left: 1rem;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.absence-time-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.time-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
}

.partial-time-inputs {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
}

.time-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.time-input-group label {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
}

.time-input {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.75rem;
  width: 80px;
}

.time-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 1px #667eea;
}

.absence-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.absence-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  min-width: 50px;
}

.absence-select {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.75rem;
  background: white;
  cursor: pointer;
}

.absence-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 1px #667eea;
}

.unauthorized-section {
  margin-top: 0.25rem;
}

.absence-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #374151;
  cursor: pointer;
}

.absence-checkbox input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.no-workers {
  text-align: center;
  padding: 1rem;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px dashed #d1d5db;
}

.shift-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
}

/* Styled action buttons */
.btn-add-workers {
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
  flex: 1;
}

.btn-add-workers:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
  transform: translateY(-1px);
}

.btn-add-workers:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.btn-delete-shift {
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
  flex: 1;
}

.btn-delete-shift:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
  transform: translateY(-1px);
}

.btn-delete-shift:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

/* Empty shift indicator styling for calendar grid */
.shift-indicator-empty {
  background: #ecacb6 !important;
  border: 2px dashed #ef5350 !important;
  opacity: 0.9;
}

.shift-indicator-empty:hover {
  opacity: 1;
  border-color: #e53935 !important;
}

/* Pending shift indicator - orange with dashed border */
.shift-indicator-pending {
  background: #ffb74d !important;
  border: 2px dashed #ff9800 !important;
}

.shift-indicator-pending:hover {
  background: #ffa726 !important;
  border-color: #f57c00 !important;
}

/* Rejected shift indicator - pale red */
.shift-indicator-rejected {
  background: #ffcdd2 !important;
  border: 2px solid #ef5350 !important;
  opacity: 0.8;
}

.shift-indicator-rejected:hover {
  background: #ef9a9a !important;
  opacity: 1;
}

.delete-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.create-shift-btn {
  padding: 0.75rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: background-color 0.2s;
  margin-top: 1rem;
}

.create-shift-btn:hover {
  background: #059669;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  z-index: 999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.message {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  z-index: 1001;
  animation: slideIn 0.3s ease;
}

.message.success {
  background: #10b981;
}

.message.error {
  background: #ef4444;
}

.message.info {
  background: #3b82f6;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@media (max-width: 768px) {
  .shift-calendar-page {
    padding: 1rem;
  }
  
  .calendar-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .calendar-container {
    flex-direction: column;
  }
  
  .calendar-day {
    min-height: 80px;
  }
  
  .day-details {
    flex: 1;
    padding: 1rem;
    max-height: none;
  }
  
  .modal-content {
    width: 95%;
  }
}
</style>